/* "Register your agency" (000082: email + password, no mobile number, no OTP).
 *   1. the account: the name, email and password of the person who will run
 *      the agency's account;
 *   2. the agency: POST /agencies/signup creates the account, the agency
 *      (PENDING_REVIEW, this person OWNER) and returns a signed-in session.
 * Nothing is sent until step 2, so going back loses nothing. The platform team
 * then checks the agency before any request reaches it. */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { signUpAgency } from '../data/api';
import { listCities, type City } from '../data/agency';
import { errorText } from '../data/words';
import { Btn, Field, Note, PasswordInput, uiCss } from '../ui';
import { Brand, toE164 } from './SignIn';
import css from './SignIn.module.css';

type Step = 'account' | 'details';
const ORDER: Step[] = ['account', 'details'];

const INPUT = `${uiCss.input}`;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Submit({ busy, children, disabled }: { busy: boolean; children: ReactNode; disabled?: boolean }) {
  return (
    <button type="submit" className={`${uiCss.btn} ${uiCss.btnPrimary} ${uiCss.btnBlock}`} disabled={busy || disabled}>
      {busy ? 'Please wait…' : children}
    </button>
  );
}

function Heading({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <div className={css.brandName} style={{ fontSize: 18 }}>
        {title}
      </div>
      <div className={css.brandSub} style={{ letterSpacing: 0, textTransform: 'none', fontSize: 12.5, marginTop: 4 }}>
        {sub}
      </div>
    </div>
  );
}

export function Register({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<Step>('account');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [cities, setCities] = useState<City[] | null>(null);
  const [citiesFailed, setCitiesFailed] = useState(false);
  const [form, setForm] = useState({
    legal_name: '',
    display_name: '',
    contact_email: '',
    contact_phone: '',
    city_code: '',
    line1: '',
    line2: '',
    pincode: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (step !== 'details' || cities || citiesFailed) return;
    listCities()
      .then(setCities)
      .catch(() => setCitiesFailed(true));
  }, [step, cities, citiesFailed]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitAccount = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (pw.length < 8) {
      setError('Use at least 8 characters for the password.');
      return;
    }
    if (pw !== pw2) {
      setError('The two passwords are not the same.');
      return;
    }
    setError(null);
    setForm((f) => ({ ...f, contact_email: f.contact_email || email.trim() }));
    setStep('details');
  };

  const submitDetails = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    signUpAgency({
      full_name: fullName.trim(),
      email: email.trim(),
      password: pw,
      legal_name: form.legal_name.trim(),
      display_name: form.display_name.trim(),
      contact_phone_e164: toE164(form.contact_phone),
      contact_email: form.contact_email.trim(),
      city_code: form.city_code.trim().toUpperCase(),
      registered_address_line_1: form.line1.trim(),
      registered_address_line_2: form.line2.trim(),
      registered_pincode: form.pincode.trim(),
    })
      .catch((err) => setError(errorText(err)))
      .finally(() => setBusy(false));
  };

  const at = ORDER.indexOf(step);
  return (
    <div className={css.page}>
      <div className={`${css.card} ${step === 'details' ? css.cardWide : ''}`}>
        <Brand sub="Register your agency" />
        <div className={css.body}>
          <div className={css.steps} aria-hidden="true" style={{ marginTop: 0 }}>
            {ORDER.map((s, i) => (
              <span key={s} className={`${css.step} ${i <= at ? css.stepOn : ''}`} />
            ))}
          </div>

          {step === 'account' ? (
            <form onSubmit={submitAccount} style={{ display: 'grid', gap: 14 }}>
              <Heading title="Your account" sub="The person who will run the agency's account signs in with this email and password." />
              <Field label="Your full name">
                <input className={`${INPUT} ${uiCss.inputLg}`} value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" required />
              </Field>
              <Field label="Email">
                <input
                  className={`${INPUT} ${uiCss.inputLg}`}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </Field>
              <Field label="Password (at least 8 characters)">
                <PasswordInput value={pw} onChange={setPw} size="lg" ariaLabel="Password" autoComplete="new-password" />
              </Field>
              <Field label="Password again">
                <PasswordInput value={pw2} onChange={setPw2} size="lg" ariaLabel="Password again" autoComplete="new-password" />
              </Field>
              {error ? <Note tone="danger">{error}</Note> : null}
              <Submit busy={busy} disabled={!fullName.trim() || !email.trim() || !pw}>
                Continue
              </Submit>
            </form>
          ) : null}

          {step === 'details' ? (
            <form onSubmit={submitDetails} style={{ display: 'grid', gap: 14 }}>
              <Heading title="About your agency" sub="The platform team checks these before any request reaches you." />
              <div className={uiCss.formGridWide}>
                <Field label="Registered (legal) name">
                  <input className={INPUT} value={form.legal_name} onChange={set('legal_name')} required />
                </Field>
                <Field label="Name families will see">
                  <input className={INPUT} value={form.display_name} onChange={set('display_name')} required />
                </Field>
                <Field label="Agency email">
                  <input className={INPUT} type="email" value={form.contact_email} onChange={set('contact_email')} required />
                </Field>
                <Field label="Agency phone">
                  <input className={INPUT} value={form.contact_phone} onChange={set('contact_phone')} inputMode="tel" required />
                </Field>
                <Field label="City">
                  {cities && cities.length > 0 ? (
                    <select className={INPUT} value={form.city_code} onChange={set('city_code')} required>
                      <option value="">Choose a city</option>
                      {cities.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={INPUT}
                      value={form.city_code}
                      onChange={set('city_code')}
                      placeholder={citiesFailed ? 'City code, e.g. JOD' : 'Loading cities…'}
                      required
                    />
                  )}
                </Field>
                <Field label="Address line 1">
                  <input className={INPUT} value={form.line1} onChange={set('line1')} required />
                </Field>
                <Field label="Address line 2 (optional)">
                  <input className={INPUT} value={form.line2} onChange={set('line2')} />
                </Field>
                <Field label="Pincode">
                  <input className={INPUT} value={form.pincode} onChange={set('pincode')} inputMode="numeric" maxLength={6} required />
                </Field>
              </div>
              {error ? <Note tone="danger">{error}</Note> : null}
              <Submit busy={busy}>Submit for review and sign in</Submit>
              <Btn block onClick={() => { setStep('account'); setError(null); }}>
                Back to your account
              </Btn>
            </form>
          ) : null}

          <Btn block onClick={onBack}>
            I already have an account — sign in
          </Btn>
        </div>
      </div>
    </div>
  );
}
